import React, {
  createContext,
  useState,
  useContext,
  useCallback,
} from "react";
import {
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
  format,
} from "date-fns";
import { router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";

export type Period = "life" | "year" | "month" | "custom";

interface StatPeriodContextType {
  selectedPeriod: Period;
  customDateRange: { from: Date; to: Date };
  updatePeriod: (
    period: Period,
    customRange?: { from: Date; to: Date }
  ) => void;
  availableYears: number[];
}

const StatPeriodContext = createContext<StatPeriodContextType | undefined>(
  undefined
);

export function StatPeriodProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { stats, user } = usePage<UserStatsPageProps>().props;
  const [selectedPeriod, setSelectedPeriod] = useState<Period>(stats.selectedPeriod);
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({ from: stats.from, to: stats.to });

  const updateDateRange = useCallback(
    (period: Period, year?: number, month?: number) => {
      const now = new Date();
      let fromDate: Date, toDate: Date;

      switch (period) {
        case "life":
          fromDate = null;
          toDate = null;
          break;
        case "year":
          fromDate = startOfYear(year ? new Date(year, 0) : now);
          toDate = endOfYear(year ? new Date(year, 0) : now);
          break;
        case "month":
          fromDate = startOfMonth(
            month !== undefined
              ? new Date(year || now.getFullYear(), month)
              : now
          );
          fromDate = endOfMonth(
            month !== undefined
              ? new Date(year || now.getFullYear(), month)
              : now
          );
          break;
        default:
          return;
      }

      setCustomDateRange({ from: fromDate, to: toDate });
      setSelectedPeriod(period);

      // Update URL
      if(fromDate && toDate) {
        const newSearchParams = new URLSearchParams();
        newSearchParams.set("from", format(fromDate, "yyyy-MM-dd"));
        newSearchParams.set("to", format(toDate, "yyyy-MM-dd"));
        router.visit(`/@${user.username}/stats?${newSearchParams.toString()}`);
      } else {
        router.visit(`/@${user.username}/stats`);
      }
    },
    [stats.from, stats.to, user.username]
  );

  const updatePeriod = useCallback(
    (period: Period, customRange?: { from: Date; to: Date }) => {
      if (customRange) {
        setCustomDateRange(customRange);
        setSelectedPeriod(period);

        // Update URL for custom range
        const newSearchParams = new URLSearchParams();
        newSearchParams.set("from", format(customRange.from, "yyyy-MM-dd"));
        newSearchParams.set("to", format(customRange.to, "yyyy-MM-dd"));
        router.visit(`/@${user.username}/stats?${newSearchParams.toString()}`);
      } else {
        updateDateRange(period);
      }
    },
    [updateDateRange]
  );

  return (
    <StatPeriodContext.Provider
      value={{ selectedPeriod, customDateRange, updatePeriod, availableYears: stats.availableYears }}
    >
      {children}
    </StatPeriodContext.Provider>
  );
}

export function useStatPeriod() {
  const context = useContext(StatPeriodContext);
  if (context === undefined) {
    throw new Error("useStatPeriod must be used within a StatPeriodProvider");
  }
  return context;
}
